import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import { authAPI } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { LoginForm, RegisterForm } from "@/components/auth";

// Helper to format API error messages
const formatApiError = (detail) => {
  if (!detail) return "Unable to connect. Please check your internet and try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  }
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "login";
  const defaultRole = searchParams.get("role") || "";
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  
  // Login form
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
    remember_me: false,
  });
  
  // Register form
  const [registerForm, setRegisterForm] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    phone: "",
    user_type: defaultRole || "",
    company_name: "",
    company_type: "logistics",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await authAPI.login({
        email: loginForm.email,
        password: loginForm.password,
        remember_me: loginForm.remember_me,
      });
      
      login(response.data.user);
      toast.success(`Welcome back, ${response.data.user.name}!`);
      
      // Redirect based on user type
      if (response.data.user.user_type === "admin") {
        navigate("/admin");
      } else if (response.data.user.user_type === "partner") {
        navigate("/partner");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      if (!error.response) {
        toast.error("Network error. Please check your connection.");
      } else if (error.response.status === 401) {
        toast.error("Invalid email or password.");
      } else if (error.response.status === 429) {
        toast.error("Too many attempts. Please wait a few minutes.");
      } else {
        toast.error(formatApiError(error.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (!registerForm.user_type) {
      toast.error("Please select how you want to use Setuxpress");
      return;
    }
    
    if (registerForm.user_type === "partner" && !registerForm.company_name) {
      toast.error("Company name is required for partners");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authAPI.register({
        email: registerForm.email,
        password: registerForm.password,
        name: registerForm.name,
        phone: registerForm.phone || null,
        user_type: registerForm.user_type,
        company_name: registerForm.user_type === "partner" ? registerForm.company_name : null,
        company_type: registerForm.user_type === "partner" ? registerForm.company_type : null,
      });
      
      login(response.data.user);
      toast.success("Account created successfully!");
      
      if (response.data.user.user_type === "partner") {
        navigate("/partner");
      } else {
        navigate("/dashboard");
      }
    } catch (error) {
      if (!error.response) {
        toast.error("Network error. Please check your connection.");
      } else if (error.response.status === 400 && error.response?.data?.detail?.includes("already")) {
        toast.error("This email is already registered. Please sign in.");
      } else {
        toast.error(formatApiError(error.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const getGoogleAuthUrl = () => {
    const redirectUrl = window.location.origin;
    return `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-new.png" alt="Setuxpress" className="h-10" />
            <span className="font-black text-xl tracking-tight">setuxpress</span>
          </Link>
          <Button variant="ghost" onClick={() => navigate("/")} className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 rounded-none">
              <TabsTrigger value="login" className="rounded-none" data-testid="login-tab">
                Sign In
              </TabsTrigger>
              <TabsTrigger value="register" className="rounded-none" data-testid="register-tab">
                Create Account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <LoginForm
                loginForm={loginForm}
                setLoginForm={setLoginForm}
                onSubmit={handleLogin}
                loading={loading}
                getGoogleAuthUrl={getGoogleAuthUrl}
              />
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <RegisterForm
                registerForm={registerForm}
                setRegisterForm={setRegisterForm}
                onSubmit={handleRegister}
                loading={loading}
                getGoogleAuthUrl={getGoogleAuthUrl}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
